import type {AxiosResponse} from 'axios';
import type {AfterResponseHook} from './types';
import message from '@/components/common/Message';
import {ERROR_MESSAGES, HTTP_STATUS, STATUS_CODE} from '@/constants';
import {clearAuthSession} from '@/services/authSession';
import {useModalStore} from '@/store/useModalStore';
import {queryClient} from '@/utils/queryClient';
import {permissionKeys} from '@/hooks/useProjectPermissions';
import {usePermissionsStore} from '@/store/usePermissionsStore';
import {refreshBenefitProfile} from '@/commerce/benefit';
import {buildPointsSceneKey} from '@/services/points/sceneKey';
import type {ApiResponse} from '@/types';

/**
 * 401 拦截器：未登录/登录过期时弹出登录框
 */
export const unauthorizedInterceptor: AfterResponseHook = (response) => {
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
        clearAuthSession();
        useModalStore.getState().toggleLoginModal(true);
    }
};

/**
 * 403 拦截器：api 接口报403无权限时，静默触发权限数据的刷新，实现前端防线自愈
 */
export const permissionInterceptor: AfterResponseHook = (response) => {
    if (response.status === HTTP_STATUS.FORBIDDEN) {
        const requestUrl = response.config.url ?? '';
        const requestMethod = response.config.method ?? 'get';

        // 不拦截拉取权限自身的接口，否则如果自身抛出 403 会导致无限死循环请求
        if (requestUrl.includes('/member/permissions')) return;

        // GET 403：弹 toast 提示，page-init 查询同时由 ErrorBoundary 渲染无权限视图
        // POST/PUT/DELETE 403：弹 toast 提示操作无权限
        if (requestMethod.toLowerCase() === 'get') {
            message.warning(ERROR_MESSAGES.PERMISSION_NO_ACCESS);
        } else {
            message.warning(ERROR_MESSAGES.PERMISSION_NO_OPERATION);
        }

        const currentProjectId = usePermissionsStore.getState().currentProjectId;
        if (currentProjectId) {
            // 静默刷新权限数据
            void queryClient.invalidateQueries({
                queryKey: permissionKeys.project(currentProjectId),
            });
        }
    }
};

/**
 * 权益不足 / 配额超限拦截器：toast + 精确刷新当前项目权益快照
 * 不主动弹订阅页，避免打扰用户当前操作
 */
export const benefitInterceptor: AfterResponseHook = (response: AxiosResponse<ApiResponse>) => {
    const body = response?.data || {};
    const code = body?.status;

    if (code !== STATUS_CODE.BENEFIT_NOT_ALLOWED && code !== STATUS_CODE.BENEFIT_QUOTA_EXCEEDED) {
        return;
    }

    // 精确刷新权益快照（用户维度 + 项目维度）
    refreshBenefitProfile();

    // toast 提示：直接用后端 message；无则用本地兜底文案
    message.warning(body.message || ERROR_MESSAGES.PERMISSION_NO_OPERATION);

    // TODO: 接入埋点
};

/**
 * 积分不足拦截器：业务码 [STATUS_CODE.CREDIT_INSUFFICIENT] 时弹出积分不足弹窗，并记录触发场景用于埋点
 */
export const pointsInsufficientInterceptor: AfterResponseHook = (
    response: AxiosResponse<ApiResponse>,
) => {
    if (response.status < 200 || response.status >= 300) return;

    const body = response.data;
    if (body?.status !== STATUS_CODE.CREDIT_INSUFFICIENT) return;

    // 计算场景 key
    const sceneKey = buildPointsSceneKey({
        url: response.config.url,
        requestData: response.config.data,
        responseData: body.data,
    });

    useModalStore.getState().openPointsInsufficientModal(sceneKey);
};
